import type { Store } from '../game/state';
import { formatTime } from '../game/progress';
import type { DifficultyLevel } from '../game/grid';

/** 游戏对外动作（设置弹层触发）。Game 实现此接口。 */
export interface GameActions {
  setDifficulty(level: DifficultyLevel): void;
  toggleRotate(enabled: boolean): void;
  toggleReference(enabled: boolean): void;
  reset(): void;
  useHint(): void;
  undo(): void;
  toggleMute(): void;
  share(): void;
  setTheme(theme: 'cozy' | 'dark' | 'light'): void;
}

/**
 * 工具栏：左侧状态（计时/步数，移动端两行）、中间进度、右侧操作按钮（等大）。
 * 难度/旋转/参考图/音效/主题/分享 收进设置弹层。
 */
export function createToolbar(
  actions: GameActions,
  store: Store,
  onOpenSettings: () => void,
): HTMLElement {
  const el = document.createElement('div');
  el.className = 'pintu-toolbar';
  el.innerHTML = `
    <div class="pintu-status">
      <div class="pintu-stat-line"><span aria-hidden="true">⏱</span><b data-time>00:00</b></div>
      <div class="pintu-stat-line">
        <span>步数</span><b data-moves>0</b>
      </div>
    </div>
    <div class="pintu-progress-wrap">
      <span class="pintu-progress" role="progressbar" aria-label="进度" aria-valuemin="0" aria-valuemax="100"><i data-bar></i></span>
    </div>
    <div class="pintu-sr" aria-live="polite" data-announce></div>
    <div class="spacer"></div>
    <div class="pintu-actions">
      <button type="button" data-hint class="pintu-act">提示</button>
      <button type="button" data-undo class="pintu-act">撤销</button>
      <button type="button" data-reset class="pintu-act">重置</button>
      <button type="button" data-settings class="pintu-act" aria-label="设置">设置</button>
    </div>
  `;

  const hintBtn = el.querySelector<HTMLButtonElement>('[data-hint]')!;
  const undoBtn = el.querySelector<HTMLButtonElement>('[data-undo]')!;
  const resetBtn = el.querySelector<HTMLButtonElement>('[data-reset]')!;
  const settingsBtn = el.querySelector<HTMLButtonElement>('[data-settings]')!;
  const tTime = el.querySelector<HTMLElement>('[data-time]')!;
  const tMoves = el.querySelector<HTMLElement>('[data-moves]')!;
  const tBar = el.querySelector<HTMLElement>('[data-bar]')!;
  const announce = el.querySelector<HTMLElement>('[data-announce]')!;
  let lastPlaced = -1;

  resetBtn.addEventListener('click', () => actions.reset());
  hintBtn.addEventListener('click', () => actions.useHint());
  undoBtn.addEventListener('click', () => actions.undo());
  settingsBtn.addEventListener('click', () => onOpenSettings());

  const update = (): void => {
    const s = store.get();
    tTime.textContent = formatTime(s.elapsedMs);
    tMoves.textContent = String(s.moves);
    const total = s.pieces.length;
    if (s.placedCount !== lastPlaced) {
      lastPlaced = s.placedCount;
      announce.textContent =
        s.placedCount === total && total > 0
          ? `完成！用时 ${formatTime(s.elapsedMs)}，${s.moves} 步`
          : `已放置 ${s.placedCount} / ${total}`;
    }
    tBar.style.width = total ? `${(s.placedCount / total) * 100}%` : '0%';
    tBar.parentElement?.setAttribute(
      'aria-valuenow',
      String(total ? Math.round((s.placedCount / total) * 100) : 0),
    );
    hintBtn.textContent = `提示(${s.hintsLeft})`;
    hintBtn.disabled = s.hintsLeft <= 0;
    undoBtn.disabled = s.history.length === 0;
  };
  store.subscribe(update);
  update();

  return el;
}
